import { useTheme } from '@mui/material';
import * as hookz from '@react-hookz/web';
import React from 'react';
import {
  type ImperativePanelHandle,
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from 'react-resizable-panels';
import { Outlet } from 'react-router-dom';
import { ErrorGuard } from '#/components/Guards';
import { ShortcutDialog } from '#/features/shortcuts';
import { SendInviteDialog } from '#/features/user-invites';
import { SupportWidget } from '#/features/user-support';
import { FeatureToggle } from '#/lib/flaggr';
import { cn, styleVars } from '#/utils/styles';
import ExplorerPanel from './components/explorer-panel';
import MobileWarningFeedback from './components/mobile-warning';
import Sidebar from './components/side-bar';
import TopBar from './components/top-bar';
import UtilityPanel from './components/utility-panel';
import { useLayout } from './use-layout';

/**
 * The DashboardLayout is the main layout for the application.
 */
function DashboardLayout() {
  const [layoutState, layoutActions] = useLayout();
  const windowSize = hookz.useWindowSize();
  const refUtilityPanel = React.useRef<ImperativePanelHandle>(null);
  const refExplorerPanel = React.useRef<ImperativePanelHandle>(null);
  const theme = useTheme();

  /*
  |------------------
  | Computed
  |------------------
  */

  const minUtilityPanelSize = React.useMemo(() => {
    return Math.min((500 / windowSize.width) * 100, 25);
  }, [windowSize.width]);

  const minExplorerPanelSize = React.useMemo(() => {
    return Math.min((300 / windowSize.width) * 100, 25);
  }, [windowSize.width]);

  const explorerPanelDefaultSize = React.useMemo(() => {
    if (layoutState.explorerPanelOpen) {
      return Math.max(minUtilityPanelSize, 25);
    }
    return 0;
  }, [layoutState.explorerPanelOpen, minUtilityPanelSize]);

  const utilityPanelDefaultSize = React.useMemo(() => {
    if (layoutState.utilityPanelOpen) {
      return Math.max(minUtilityPanelSize, 25);
    }
    return 0;
  }, [layoutState.utilityPanelOpen, minUtilityPanelSize]);

  const mainContentDefaultSize = React.useMemo(() => {
    const subtractAmount = explorerPanelDefaultSize + utilityPanelDefaultSize;
    return 100 - subtractAmount;
  }, [explorerPanelDefaultSize, utilityPanelDefaultSize]);

  /*
  |------------------
  | Effects
  |------------------
  */

  React.useEffect(() => {
    if (refExplorerPanel.current) {
      if (layoutState.explorerPanelOpen) {
        refExplorerPanel.current.expand();
      } else {
        refExplorerPanel.current.collapse();
      }
    }
  }, [layoutState.explorerPanelOpen]);

  React.useEffect(() => {
    if (refUtilityPanel.current) {
      if (layoutState.utilityPanelOpen) {
        refUtilityPanel.current.expand();
      } else {
        refUtilityPanel.current.collapse();
      }
    }
  }, [layoutState.utilityPanelOpen]);

  return (
    <div className='flex overflow-hidden'>
      <TopBar />
      <Sidebar />
      <Main>
        <PanelGroup direction='horizontal' autoSaveId='dashboard-layout'>
          <Panel
            id='explorer-panel'
            order={1}
            defaultSize={explorerPanelDefaultSize}
            minSize={minExplorerPanelSize}
            ref={refExplorerPanel}
            collapsible
            collapsedSize={0}
            onCollapse={() => {
              if (layoutState.explorerPanelOpen) {
                layoutActions.closeExplorerPanel();
              }
            }}
          >
            <ExplorerPanel />
          </Panel>
          <PanelResizeHandle disabled={!layoutState.explorerPanelOpen} />
          <Panel
            id='main-content-panel'
            order={2}
            defaultSize={mainContentDefaultSize}
            minSize={50}
            className='max-h-(--main-content-panel-max-height) overflow-y-auto'
            style={styleVars({
              '--main-content-panel-max-height': `calc(100vh - ${theme.measurements.topBar.height}px)`,
            })}
          >
            <ErrorGuard>
              <Outlet />
            </ErrorGuard>
            {!layoutState.utilityPanelOpen && <SupportWidget />}
            <SendInviteDialog />
            <ShortcutDialog />
          </Panel>
          <FeatureToggle flag='ai_chat_panel' fallback={null}>
            <PanelResizeHandle disabled={!layoutState.utilityPanelOpen} />
            <Panel
              ref={refUtilityPanel}
              id='chat-panel'
              className={cn('border-l border-divider')}
              order={3}
              defaultSize={utilityPanelDefaultSize}
              minSize={minUtilityPanelSize}
              collapsible
              collapsedSize={0}
              onCollapse={() => {
                if (layoutState.utilityPanelOpen) {
                  layoutActions.closeUtilityPanel();
                }
              }}
              onExpand={() => {
                if (!layoutState.utilityPanelOpen) {
                  layoutActions.openUtilityPanel({ type: 'chat' });
                }
              }}
            >
              <UtilityPanel />
            </Panel>
          </FeatureToggle>
        </PanelGroup>
      </Main>
      <MobileWarningFeedback />
    </div>
  );
}

export default DashboardLayout;

/*
|------------------
| Internal Components
|------------------
*/

/**
 * Main content section for the dashboard layout.
 */
function Main({ children }: React.PropsWithChildren) {
  const theme = useTheme();
  return (
    <main
      className='w-full p-0 min-h-(--layout-main-min-height) mt-(--layout-main-mt) max-w-(--layout-main-max-width)'
      style={styleVars({
        '--layout-main-min-height': `calc(100vh - ${theme.measurements.topBar.height}px)`,
        '--layout-main-max-width': `calc(100vw - ${theme.measurements.sideBar.width}px)`,
        '--layout-main-mt': `${theme.measurements.topBar.height}px`,
      })}
    >
      <div className='h-full w-full overflow-y-auto'>{children}</div>
    </main>
  );
}
